import { useState, useRef, useCallback } from 'react';
import { ScanText, Upload, Sparkles, ChevronDown, ChevronUp, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import type { QuestionForm } from '../../lib/questionForm';

// ── OCR Parser ──────────────────────────────────────────────────────────────
export interface ParsedQuestion {
  questionText: string;
  options: string[];
}

interface OcrApiResult {
  text: string;
  method: string;
  parsed?: {
    questionText: string;
    options: string[];
    detectedLanguage: 'ko' | 'mixed';
  };
  requiresManualReview?: boolean;
}

export function parseOcrText(raw: string): ParsedQuestion {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  // Try to split by circled-number markers ①②③④
  const markers = ['①', '②', '③', '④'];
  const joined = lines.join('\n');
  const hasMarkers = markers.some((m) => joined.includes(m));

  if (hasMarkers) {
    // Split around markers
    const parts: string[] = [];
    let remaining = joined;
    for (let i = markers.length - 1; i >= 0; i--) {
      const idx = remaining.lastIndexOf(markers[i]);
      if (idx !== -1) {
        parts.unshift(remaining.slice(idx + markers[i].length).trim());
        remaining = remaining.slice(0, idx).trim();
      }
    }
    // remaining is the question text
    return {
      questionText: remaining.replace(/^\d+[.)\s]+/, '').trim(),
      options: parts.slice(0, 4),
    };
  }

  // Fallback: numbered options like 1) 2) 3) 4) or 1. 2. 3. 4.
  const optLines: string[] = [];
  const qLines: string[] = [];
  const optRegex = /^[([]?\s*[1-4]\s*[)\].:]\s*/;
  for (const line of lines) {
    if (optRegex.test(line)) {
      optLines.push(line.replace(optRegex, '').trim());
    } else {
      qLines.push(line);
    }
  }

  if (optLines.length >= 2) {
    return {
      questionText: qLines
        .join(' ')
        .replace(/^\d+[.)\s]+/, '')
        .trim(),
      options: optLines.slice(0, 4),
    };
  }

  // Last resort: first line = question, rest = options
  return {
    questionText: (lines[0] || '').replace(/^\d+[.)\s]+/, '').trim(),
    options: lines.slice(1, 5),
  };
}

// ── OCR Panel ───────────────────────────────────────────────────────────────
interface OcrFillPanelProps {
  form: QuestionForm;
  setForm: (f: QuestionForm) => void;
  onApplied: (question: QuestionForm['question'], options: QuestionForm['options']) => void;
}

export default function OcrFillPanel({ form, setForm, onApplied }: OcrFillPanelProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState<ParsedQuestion | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setRawText('');
    setParsed(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(f);
  }, []);

  const handleExtract = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/ocr/extract', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const resultData = (data.data ?? {}) as OcrApiResult;
      const text: string = resultData.text || '';
      setRawText(text);
      const result = resultData.parsed
        ? {
            questionText: resultData.parsed.questionText || '',
            options: (resultData.parsed.options || []).slice(0, 4),
          }
        : parseOcrText(text);
      setParsed(result);
      toast.success(`Extracted ${text.length} chars (${resultData.method || 'ocr'})`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'OCR failed'));
    } finally {
      setLoading(false);
    }
  };

  const applyToForm = () => {
    if (!parsed) return;
    // OCR output is always text, so every option becomes text-only — including
    // the ones OCR found no text for. Rewriting all four (rather than just the
    // parsed ones) keeps the four option formats uniform and clears any
    // image/audio URL left over from a previous format, mirroring
    // `applyBulkPaste` in QuestionsPage.tsx.
    const newOptions = form.options.map((option, i) => ({
      ...option,
      format: 'text',
      text: parsed.options[i] ?? '',
      imageUrl: '',
      audioUrl: '',
    }));
    setForm({
      ...form,
      // Auto-extracted content is never a deliberate admin type choice, and it
      // may no longer match a previously-picked preset (which the server-side
      // cross-field validator would then reject).
      questionType: null,
      question: { ...form.question, format: 'text', text: parsed.questionText },
      options: newOptions,
    });
    onApplied({ ...form.question, format: 'text', text: parsed.questionText }, newOptions);
    toast.success('Question & options filled from OCR');
    setOpen(false);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setRawText('');
    setParsed(null);
  };

  return (
    <div className="border border-dashed border-blue-300 rounded-xl bg-blue-50/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50/60 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-blue-700">
          <ScanText size={16} />
          Fill from Exam Image (OCR)
        </span>
        {open ? (
          <ChevronUp size={16} className="text-blue-400" />
        ) : (
          <ChevronDown size={16} className="text-blue-400" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {/* Upload area */}
          <div className="flex gap-3">
            <div
              onClick={() => inputRef.current?.click()}
              className="flex-1 border-2 border-dashed border-blue-200 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors bg-white min-h-[120px] flex items-center justify-center"
            >
              {preview ? (
                <img src={preview} alt="Preview" className="max-h-32 mx-auto rounded" />
              ) : (
                <div className="space-y-1">
                  <Upload size={24} className="mx-auto text-blue-300" />
                  <p className="text-xs text-blue-400">Upload exam image</p>
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
            />

            <div className="flex flex-col gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExtract}
                disabled={!file || loading}
                className="btn-primary text-xs flex items-center gap-1.5 !py-2"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <ScanText size={14} />}
                {loading ? 'Extracting...' : 'Extract'}
              </button>
              {file && (
                <button
                  type="button"
                  onClick={reset}
                  className="btn-secondary text-xs flex items-center gap-1 !py-2"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Raw OCR output */}
          {rawText && (
            <details className="text-xs">
              <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                Raw OCR output
              </summary>
              <pre className="mt-1 p-2 bg-gray-50 rounded-lg text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto font-korean">
                {rawText}
              </pre>
            </details>
          )}

          {/* Parsed preview + Apply */}
          {parsed && (
            <div className="bg-white rounded-lg border border-blue-200 p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                <Sparkles size={12} className="text-amber-500" /> Parsed Result
              </p>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Question:</p>
                <p className="text-sm text-gray-800 font-medium font-korean">
                  {parsed.questionText || <span className="text-gray-300 italic">empty</span>}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="text-xs bg-gray-50 rounded px-2 py-1.5">
                    <span className="text-gray-400">{'①②③④'[i]} </span>
                    <span className="text-gray-700 font-korean">{parsed.options[i] || '—'}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={applyToForm}
                className="btn-primary text-xs w-full flex items-center justify-center gap-1.5 !py-2 mt-1"
              >
                <Sparkles size={13} /> Apply to Form
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
